import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useCenter } from "@/context/CenterContext";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CheckCircle2, Lock, Unlock, Clock, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { createPortal } from "react-dom";

const money = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;
const hrs = (n) => `${(Math.round((n || 0) * 100) / 100).toFixed(2)}h`;

// Time combobox: type an exact time (e.g. 7:01) OR pick from a 30-min preset dropdown. 24h, no AM/PM.
function TimeField({ value, onCommit, disabled, testid }) {
  const [v, setV] = React.useState(value || "");
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const lastRef = React.useRef(value || "");
  React.useEffect(() => { setV(value || ""); lastRef.current = value || ""; }, [value]);

  const options = React.useMemo(() => {
    const a = [];
    for (let h = 0; h < 24; h++) for (const m of [0, 30]) a.push(String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"));
    return a;
  }, []);

  const normalize = (raw) => {
    if (!raw) return "";
    let hh, mm;
    if (raw.includes(":")) {
      const [a, b] = raw.split(":");
      hh = parseInt(a || "0", 10); mm = parseInt(b || "0", 10);
    } else {
      const d = raw.replace(/\D/g, "");
      if (d.length <= 2) { hh = parseInt(d || "0", 10); mm = 0; }
      else if (d.length === 3) { hh = parseInt(d.slice(0, 1), 10); mm = parseInt(d.slice(1), 10); }
      else { hh = parseInt(d.slice(0, 2), 10); mm = parseInt(d.slice(2, 4), 10); }
    }
    if (isNaN(hh)) hh = 0;
    if (isNaN(mm)) mm = 0;
    hh = Math.min(23, Math.max(0, hh)); mm = Math.min(59, Math.max(0, mm));
    return String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0");
  };

  const commit = (raw) => {
    const val = raw !== undefined ? raw : v;
    if (!val) { setV(""); return; }
    const norm = normalize(val);
    setV(norm);
    if (norm !== lastRef.current) { lastRef.current = norm; onCommit(norm); }
  };

  const openList = () => {
    const r = inputRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 96) });
    setOpen(true);
  };
  const pick = (opt) => { setOpen(false); setV(opt); if (opt !== lastRef.current) { lastRef.current = opt; onCommit(opt); } };

  React.useEffect(() => {
    if (open && listRef.current) {
      const sel = listRef.current.querySelector('[data-selected="true"]');
      if (sel) sel.scrollIntoView({ block: "center" });
    }
  }, [open]);

  return (
    <div className="relative">
      <Input ref={inputRef} value={v} disabled={disabled}
        onChange={(e) => setV(e.target.value.replace(/[^0-9:]/g, "").slice(0, 5))}
        onFocus={openList}
        onKeyDown={(e) => { if (e.key === "Enter") { commit(); setOpen(false); e.target.blur(); } if (e.key === "Escape") setOpen(false); }}
        onBlur={() => commit()}
        placeholder="--:--" inputMode="numeric"
        className="h-8 w-24 text-center tabular-nums" data-testid={testid} />
      {open && !disabled && pos && createPortal(
        <>
          <div className="fixed inset-0 z-40" onMouseDown={() => { commit(); setOpen(false); }} />
          <div ref={listRef} style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width }}
            className="z-50 max-h-56 overflow-y-auto rounded-md border border-border bg-popover shadow-xl scrollbar-thin" data-testid={`${testid}-list`}>
            {options.map((o) => (
              <button key={o} type="button" data-selected={o === v} onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-accent ${o === v ? "bg-accent font-semibold" : ""}`}>{o}</button>
            ))}
          </div>
        </>, document.body)}
    </div>
  );
}

export default function Presence() {
  const { t, lang } = useT();
  const { role, isSuperAdmin } = useCenter();
  const isAdmin = isSuperAdmin || role === "admin";
  const [employees, setEmployees] = useState([]);
  const [idx, setIdx] = useState(0);
  const [offset, setOffset] = useState(0);
  const [period, setPeriod] = useState(null);
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const draftsRef = useRef({});
  useEffect(() => { draftsRef.current = drafts; }, [drafts]);

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data.filter((e) => e.active !== false)));
    api.get("/timeclock/config").then((r) => setPeriod(r.data));
  }, []);

  const emp = employees[idx];

  const bounds = useCallback(() => {
    if (!period) return null;
    let { start, end } = period.current_period;
    const days = period.pay_period_type === "biweekly" ? 14 : 7;
    if (offset !== 0) {
      const s = new Date(start + "T00:00:00"); s.setDate(s.getDate() + offset * days);
      const e = new Date(end + "T00:00:00"); e.setDate(e.getDate() + offset * days);
      start = s.toISOString().slice(0, 10); end = e.toISOString().slice(0, 10);
    }
    return { start, end };
  }, [period, offset]);

  const load = useCallback(() => {
    const b = bounds();
    if (!emp || !b) return;
    api.get("/timeclock/timesheet", { params: { user_id: emp.id, start: b.start, end: b.end } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [emp, bounds]);
  useEffect(() => { load(); }, [load]);

  const editTime = async (entry, field, hhmm) => {
    const base = (entry[field] || entry.clock_in).slice(0, 10);
    try { await api.put(`/timeclock/entries/${entry.id}`, { [field]: `${base}T${hhmm}:00` }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const approveAll = async () => {
    const ids = [];
    (data?.days || []).forEach((d) => d.entries.forEach((e) => { if (e.clock_out && e.status !== "approved") ids.push(e.id); }));
    if (!ids.length) return toast.info(t("noTimeEntries"));
    await api.post("/timeclock/entries/approve-bulk", { entry_ids: ids }); toast.success(t("approveAll")); load();
  };
  const approve = async (id) => { try { await api.post(`/timeclock/entries/${id}/approve`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const closePeriod = async () => {
    if (!window.confirm(`${t("closePeriod")}?`)) return;
    try { await api.post("/timeclock/close-period", { period_start: data.start }); toast.success(t("periodClosed")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reopen = async () => { await api.post("/timeclock/reopen-period", { period_start: data.start }); load(); };
  const setDraftAndSave = (dateStr, field, val) => {
    const cur = { ...(draftsRef.current[dateStr] || {}), [field]: val };
    if (cur.in && cur.out && emp) {
      setDrafts((prev) => { const { [dateStr]: _omit, ...rest } = prev; return rest; });
      api.post("/timeclock/entries", { user_id: emp.id, clock_in: `${dateStr}T${cur.in}:00`, clock_out: `${dateStr}T${cur.out}:00` })
        .then(() => { load(); toast.success(t("save")); })
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    } else {
      setDrafts((prev) => ({ ...prev, [dateStr]: cur }));
    }
  };

  const fmtDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { weekday: "short", day: "numeric", month: "short" });
  const fmtRange = (s, e) => `${new Date(s + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "numeric", month: "short" })} – ${new Date(e + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { day: "numeric", month: "short", year: "numeric" })}`;

  const closed = data?.closed;

  return (
    <div className="flex flex-col lg:flex-row h-full overflow-hidden">
      {/* Left employee card */}
      <aside className="lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-border p-5 overflow-y-auto scrollbar-thin">
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => setIdx((i) => (i - 1 + employees.length) % employees.length)} disabled={employees.length < 2} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-accent disabled:opacity-40" data-testid="prev-employee"><ChevronLeft className="w-4 h-4" /></button>
            <UserAvatar name={emp?.name} src={emp?.profile_picture} size={72} />
            <button onClick={() => setIdx((i) => (i + 1) % employees.length)} disabled={employees.length < 2} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:bg-accent disabled:opacity-40" data-testid="next-employee"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <h2 className="font-display text-xl font-bold" data-testid="timesheet-employee-name">{emp?.name || "—"}</h2>
          {data?.employee?.primary_position && (
            <span className="mt-1 inline-flex items-center gap-1.5 text-xs" style={{ color: data.employee.primary_position.color }}>
              <span className="w-2 h-2 rounded-full" style={{ background: data.employee.primary_position.color }} />{data.employee.primary_position.name}
            </span>
          )}
        </div>
        <div className="mt-6 rounded-xl border border-border p-4 space-y-3 text-left">
          <div><p className="text-[11px] text-muted-foreground">{t("branch")}</p><p className="text-sm font-medium">{data?.employee?.branch || "—"}</p></div>
          <div><p className="text-[11px] text-muted-foreground">NIP</p><p className="text-sm font-bold tracking-wide">{data?.employee?.nip || "—"}</p></div>
          <div className="pt-2 border-t border-border"><p className="text-[11px] text-muted-foreground">{t("hourlyRate")}</p><p className="text-sm font-semibold text-primary">{data?.employee?.hourly_rate ? money(data.employee.hourly_rate) + " / h" : "—"}</p></div>
        </div>
      </aside>

      {/* Right timesheet */}
      <main className="flex-1 overflow-y-auto scrollbar-thin p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold tracking-tight">{t("timesheet")}</h1>
            {closed && <Badge className="bg-slate-500">{t("periodClosed")}</Badge>}
          </div>
          {!closed
            ? <Button onClick={approveAll} className="bg-orange-400 hover:bg-orange-500" data-testid="approve-all-btn">{t("approveAll")}</Button>
            : isAdmin && <Button variant="outline" onClick={reopen} data-testid="reopen-period"><Unlock className="w-4 h-4 mr-1.5" />{t("reopenPeriod")}</Button>}
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2 mb-5">
          <Button variant="outline" size="icon" onClick={() => setOffset((o) => o - 1)} data-testid="period-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="px-4 py-2 rounded-lg border border-border text-sm font-medium min-w-56 text-center flex items-center justify-center gap-2" data-testid="period-label">
            {data ? fmtRange(data.start, data.end) : "—"}
            {offset === 0 && <Badge className="bg-primary/15 text-primary text-[10px]">{t("currentPeriod")}</Badge>}
          </div>
          <Button variant="outline" size="icon" onClick={() => setOffset((o) => o + 1)} data-testid="period-next"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setOffset(0)}>{t("today")}</Button>
          {isAdmin && offset < 0 && !closed && <Button variant="outline" size="sm" className="ml-auto" onClick={closePeriod} data-testid="close-period"><Lock className="w-4 h-4 mr-1.5" />{t("closePeriod")}</Button>}
        </div>

        {/* Summary cards */}
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="rounded-xl border border-border p-4">
            <p className="font-display font-bold mb-3">{t("payableHours")}</p>
            <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground">{t("clockedHours")}</span><span className="font-medium">{hrs(data?.clocked_hours)}</span></div>
            <div className="flex justify-between text-sm py-1 border-t border-border mt-2 pt-2"><span className="font-semibold">{t("payableHours")}</span><span className="font-bold">{hrs(data?.clocked_hours)}</span></div>
          </div>
          <div className="rounded-xl border border-border p-4">
            <p className="font-display font-bold mb-3">{t("payPeriod")}</p>
            <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{t("scheduledHours")}</span><span className="font-medium">{hrs(data?.scheduled_hours)}</span></div>
            {data?.unapproved > 0 && <div className="flex justify-between text-sm py-1"><span className="text-muted-foreground">{t("unapproved")}</span><Badge variant="secondary">{data.unapproved}</Badge></div>}
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="grid grid-cols-[1fr_1.2fr_1.6fr_0.8fr] bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <div className="p-3">{t("date")}</div><div className="p-3 border-l border-border">{t("schedule")}</div>
            <div className="p-3 border-l border-border">{t("clockedTime")}</div><div className="p-3 border-l border-border text-right">{t("hoursWorked")}</div>
          </div>
          {(data?.days || []).length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">{t("noTimeEntries")}</div>}
          {(data?.days || []).map((day) => {
            const rows = Math.max(day.shifts.length, day.entries.length, 1);
            const dayRows = Array.from({ length: rows }).map((_, i) => {
              const sh = day.shifts[i]; const en = day.entries[i];
              return (
                <div key={day.date + i} className="grid grid-cols-[1fr_1.2fr_1.6fr_0.8fr] border-t border-border text-sm items-center" data-testid="timesheet-row">
                  <div className="p-3 font-medium text-primary">{i === 0 ? fmtDay(day.date) : ""}</div>
                  <div className="p-3 border-l border-border">
                    {sh ? <span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: sh.position_color || "#94a3b8" }} />{sh.start_time}–{sh.end_time}</span> : <span className="text-muted-foreground">—</span>}
                  </div>
                  <div className="p-3 border-l border-border">
                    {en ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <TimeField value={en.clock_in?.slice(11, 16)} disabled={closed} testid="ts-in"
                          onCommit={(v) => editTime(en, "clock_in", v)} />
                        <span className="text-muted-foreground">–</span>
                        {en.clock_out
                          ? <TimeField value={en.clock_out?.slice(11, 16)} disabled={closed} testid="ts-out"
                              onCommit={(v) => editTime(en, "clock_out", v)} />
                          : <Badge variant="outline" className="text-amber-600">{t("clockedInNow")}</Badge>}
                        {en.clock_in_loc?.within === false && <Badge className="bg-red-500/15 text-red-600 text-[9px]"><MapPin className="w-2.5 h-2.5 mr-0.5" />{Math.round(en.clock_in_loc.distance)}m</Badge>}
                        {en.edited && <Badge variant="secondary" className="text-[9px]">{t("edited")}</Badge>}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <TimeField value={drafts[day.date]?.in || ""} disabled={closed} testid="ts-in-new"
                          onCommit={(v) => setDraftAndSave(day.date, "in", v)} />
                        <span className="text-muted-foreground">–</span>
                        <TimeField value={drafts[day.date]?.out || ""} disabled={closed} testid="ts-out-new"
                          onCommit={(v) => setDraftAndSave(day.date, "out", v)} />
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-l border-border text-right flex items-center justify-end gap-2">
                    <span className="font-semibold">{en ? hrs(en.hours) : ""}</span>
                    {en && en.clock_out && !closed && (en.status === "approved"
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      : <button onClick={() => approve(en.id)} data-testid="approve-entry" title={t("approve")}><CheckCircle2 className="w-4 h-4 text-muted-foreground hover:text-emerald-500" /></button>)}
                  </div>
                </div>
              );
            });
            return (
              <React.Fragment key={day.date}>
                {dayRows}
              </React.Fragment>
            );
          })}
          {/* Totals */}
          <div className="grid grid-cols-[1fr_1.2fr_1.6fr_0.8fr] border-t-2 border-border bg-muted/60 text-sm font-bold items-center">
            <div className="p-3 flex items-center gap-1.5">{data?.unapproved > 0 && <AlertTriangle className="w-4 h-4 text-amber-500" />}{t("totalHours")}</div>
            <div className="p-3 border-l border-border" />
            <div className="p-3 border-l border-border text-right text-primary text-base" data-testid="timesheet-pay">{money(data?.pay)}</div>
            <div className="p-3 border-l border-border text-right" data-testid="timesheet-total-hours">{hrs(data?.clocked_hours)}</div>
          </div>
        </div>
      </main>
    </div>
  );
}
